"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type FormEvent } from "react";
import {
  ApiError,
  fetchUsageSummary,
  listMyJobs,
  startAnalyze,
  startImageAudit,
  type JobSummary,
  type UsageSummary,
} from "../../lib/api";
import type { RunMode, Viewport } from "../../lib/audit-types";
import { isOurOwnDomain, SELF_AUDIT_MESSAGE } from "../../lib/self-domain";
import { validateAuditUrl } from "../../lib/guest-audit";
import {
  isWarnPreview,
  SiteFavicon,
  SitePreviewMessage,
  useSitePreflight,
} from "../../components/site-preview";
import { clearAuth, readClientId } from "../../lib/auth";
import { copyShareLink } from "../../lib/share";
import { AppToMarketingLink } from "../components/app-to-marketing-link";
import { FigmaOriginChip } from "../components/figma-origin-chip";
import {
  SettingLabel,
  OutputGlyph,
  DeviceGlyph,
  PagesGlyph,
  openUpgradeInNewTab,
  chipLabelFor,
  loadAuditDefaults,
  loadDisplayName,
} from "../components/setting-label";
import { isInTrash, trashJob } from "../../lib/trash";
import { useAppAuth } from "../app-shell";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type LoadState =
  | { status: "loading" }
  | { status: "ready"; jobs: JobSummary[] }
  | { status: "error"; message: string };

type ViewportOption = "desktop" | "mobile" | "both";

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const RECENT_URLS_KEY = "snap-site:web-recent-urls";
const MAX_RECENT_URLS = 6;

// Page-count chips the user can pick from. Mirrors the audit-config page
// so anyone landing on /audit/new via a deep link still sees the same
// options.
const PAGE_OPTIONS = [1, 3, 5, 10] as const;
const AI_EXAMPLES = ["Capture pricing & checkout", "Open the mobile menu", "Switch pricing to annual"];

// Image-upload constraints — must mirror backend /jobs/image validation.
const IMAGE_ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;
const IMAGE_ACCEPT_ATTR = IMAGE_ACCEPTED_TYPES.join(",");
const IMAGE_MAX_BYTES = 4_500_000;

// Top-level input source. The user picks whether they're starting from
// a URL (the dominant path) or uploading a screenshot. Each path has
// genuinely different inputs and different settings, which is why it's
// tabs rather than a chip inside the settings panel — a chip would have
// implied this was a tweak rather than a fundamental fork.
type InputSource = "url" | "image";

const TIPS: { body: string; link: string; href: string }[] = [
  {
    body: "Use “Capture hidden states” to open menus, modals, or pricing toggles for a more complete review.",
    link: "Learn more",
    href: "/about",
  },
  {
    body: "Run both Desktop + Mobile viewports to catch responsive issues that only show on smaller screens.",
    link: "See plans",
    href: "/pricing",
  },
  {
    body: "Share your audit with stakeholders using a single link — no login required for viewers.",
    link: "How sharing works",
    href: "/about",
  },
  {
    body: "Use the Annotate tool on the canvas to add your own findings alongside the AI audit.",
    link: "Learn more",
    href: "/about",
  },
];

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function greeting(name?: string | null): string {
  const h = new Date().getHours();
  const tod = h < 12 ? "morning" : h < 17 ? "afternoon" : "evening";
  return name ? `Good ${tod}, ${name}` : `Good ${tod}`;
}

/**
 * Best-effort first name from an email. Returns null when we can't
 * recover a real word — picking "there" as a fallback produced
 * "Good morning, there" which read like a typo. With null, greeting()
 * cleanly drops the trailing comma.
 */
function humanizeName(email: string): string | null {
  const local = email.split("@")[0] ?? "";
  const cleaned = local
    .replace(/[._-]/g, " ")
    .replace(/\d+$/g, "")
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
  return cleaned || null;
}

function labelForStatus(s: JobSummary["status"]): string {
  return s === "done" ? "Done" : s === "error" ? "Failed" : s === "running" ? "Running" : "Queued";
}

function hostnameFor(url: string): string {
  try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return url; }
}

function relativeTime(when: number): string {
  const diff = Date.now() - when;
  const sec = Math.round(diff / 1000);
  if (sec < 45) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.round(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.round(mo / 12)}y ago`;
}

function pushRecentUrl(value: string) {
  try {
    const raw = window.localStorage.getItem(RECENT_URLS_KEY);
    const prev: string[] = raw ? JSON.parse(raw) : [];
    const next = [value, ...prev.filter((r) => r !== value)].slice(0, MAX_RECENT_URLS);
    window.localStorage.setItem(RECENT_URLS_KEY, JSON.stringify(next));
  } catch { /* ignore */ }
}

function viewportsForOption(v: ViewportOption): Viewport[] {
  return v === "both" ? ["desktop", "mobile"] : [v];
}

function modeLabel(m?: RunMode): string {
  return m === "snapshot" ? "Screenshots only" : m === "imageAudit" ? "Image audit" : "UX Audit";
}

function findingsLabel(job: JobSummary): {
  text: string;
  tone: "good" | "warn" | "error" | "none" | "neutral" | "capture";
  tooltip?: string;
} {
  // Match the Files list exactly: "Failed" (not "Error") with the
  // failure reason as a tooltip, so an audit reads the same on both
  // surfaces.
  if (job.status === "error")
    return {
      text: "Failed",
      tone: "error",
      tooltip: job.error || "The capture failed. Try running this audit again.",
    };
  // Distinct from Failed: the run completed but every page came back blank.
  // Its own state + tone so it doesn't masquerade as a clean Done and
  // doesn't get lumped under the hard-error "Failed".
  if (job.captureFailed)
    return {
      text: "Couldn't capture",
      tone: "capture",
      tooltip:
        "Every page came back blank, so the credits were refunded. Open it to upload a screenshot instead.",
    };
  if (job.status !== "done") return { text: labelForStatus(job.status), tone: "none" };
  // Snapshot runs never produce findings by design — same logic the
  // Files list uses. Em dash + tooltip keeps the row honest about
  // its state without falsely celebrating with green "No findings."
  if (job.runMode === "snapshot") {
    return {
      text: "—",
      tone: "neutral",
      tooltip: "Screenshots only — no audit was run. You can add findings manually.",
    };
  }
  const count = job.findingsCount;
  if (typeof count === "number") {
    if (count === 0) return { text: "No findings", tone: "good" };
    return { text: `${count} finding${count === 1 ? "" : "s"}`, tone: "warn" };
  }
  // Backwards-compat: older API responses (pre-findingsCount deploy) only
  // carried `score`. Show a neutral count-less label rather than mislabeling
  // the score as findings.
  return { text: "View audit", tone: "none" };
}

function pickTip(jobCount: number): (typeof TIPS)[0] {
  // Rotate by date so the same user sees a fresh tip across days,
  // mixed with their job count so heavy users still get variety
  // mid-week. Day-of-year keeps the rotation stable within a day.
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  const dayOfYear = Math.floor((now.getTime() - start.getTime()) / 86_400_000);
  return TIPS[(dayOfYear + jobCount) % TIPS.length];
}

/* ------------------------------------------------------------------ */
/*  Root component                                                     */
/* ------------------------------------------------------------------ */

export function DashboardClient() {
  const auth = useAppAuth();
  const [jobs, setJobs] = useState<LoadState>({ status: "loading" });
  const [lifetime, setLifetime] = useState<UsageSummary["lifetime"] | null>(null);
  // Display name chosen in Settings → Profile (localStorage). Read on mount;
  // null until then so the greeting falls back to the email-derived name.
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<JobSummary | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Welcome banner shown once after first sign-in (flag set by the
  // login flow). Local boolean so dismissal feels instant — the
  // localStorage write happens on the same tick but UI doesn't wait.
  const [showWelcome, setShowWelcome] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (window.localStorage.getItem("snap-site:welcome-pending")) {
        setShowWelcome(true);
      }
    } catch {
      /* localStorage blocked — fine, just don't show the banner. */
    }
  }, []);
  const dismissWelcome = useCallback(() => {
    setShowWelcome(false);
    try {
      window.localStorage.removeItem("snap-site:welcome-pending");
      window.localStorage.setItem("snap-site:welcome-completed", "1");
    } catch {
      /* ignore */
    }
  }, []);

  const token = auth.status === "authenticated" ? auth.token : null;

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    listMyJobs(token)
      .then((list) => { if (!cancelled) setJobs({ status: "ready", jobs: list }); })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          clearAuth();
          return;
        }
        setJobs({ status: "error", message: err instanceof Error ? err.message : "Could not load your audits." });
      });
    return () => { cancelled = true; };
  }, [token]);

  // Pick up the Settings display name on mount (and when the window regains
  // focus, so editing it in another tab reflects here without a reload).
  useEffect(() => {
    const sync = () => setDisplayName(loadDisplayName());
    sync();
    window.addEventListener("focus", sync);
    return () => window.removeEventListener("focus", sync);
  }, []);

  // Lifetime activity totals come from the persistent usage summary (counts
  // trashed audits too) so the "Your activity" card doesn't shrink when a
  // project is deleted. Best-effort: if it fails we fall back to the
  // job-list-derived counts below.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    fetchUsageSummary(token)
      .then((summary) => { if (!cancelled) setLifetime(summary.lifetime ?? null); })
      .catch(() => { /* non-fatal — stats fall back to the live job list */ });
    return () => { cancelled = true; };
  }, [token]);

  function retryLoad() {
    if (!token) return;
    setJobs({ status: "loading" });
    listMyJobs(token)
      .then((list) => setJobs({ status: "ready", jobs: list }))
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) {
          clearAuth();
          return;
        }
        setJobs({ status: "error", message: err instanceof Error ? err.message : "Could not load your audits." });
      });
  }

  function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      trashJob(pendingDelete);
      setJobs((prev) => prev.status === "ready" ? { status: "ready", jobs: prev.jobs.filter((j) => j.id !== pendingDelete.id) } : prev);
      setPendingDelete(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Could not move to trash.");
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  }

  const allJobs = useMemo(() => (jobs.status === "ready" ? jobs.jobs.filter((j) => !isInTrash(j.id)) : []), [jobs]);

  const recentJobs = useMemo(
    () => [...allJobs].sort((a, b) => b.createdAt - a.createdAt),
    [allJobs],
  );

  // "Recent:" chips reflect what the user ACTUALLY audited — including
  // runs started from the homepage hero, the Figma plugin, or another
  // browser — by deriving from the job history instead of trusting the
  // per-browser localStorage trail alone. Distinct by hostname, newest
  // first; image audits skipped (no URL to refill the form with).
  const recentJobUrls = useMemo(() => {
    const urls: string[] = [];
    const seen = new Set<string>();
    for (const j of recentJobs) {
      if (!j.sourceUrl || j.runMode === "imageAudit") continue;
      const host = hostnameFor(j.sourceUrl);
      if (seen.has(host)) continue;
      seen.add(host);
      urls.push(j.sourceUrl);
      if (urls.length >= MAX_RECENT_URLS) break;
    }
    return urls;
  }, [recentJobs]);

  // Mirror the derived list into localStorage so /audit/new — which only
  // reads the stored trail — stays fresh too.
  useEffect(() => {
    if (recentJobUrls.length === 0) return;
    try {
      window.localStorage.setItem(RECENT_URLS_KEY, JSON.stringify(recentJobUrls));
    } catch { /* ignore */ }
  }, [recentJobUrls]);

  /* Stats for returning users. `last30` adds a velocity stat without
     adding a third number to the grid — it gets rendered as a caption
     below the two big totals, contextualizing them with recent
     activity instead of leaving them as pure lifetime counts. */
  const stats = useMemo(() => {
    const running = allJobs.filter((j) => j.status === "running").length;
    const sites = new Set(allJobs.map((j) => hostnameFor(j.sourceUrl))).size;
    const thirtyDaysAgo = Date.now() - 30 * 86_400_000;
    // Prefer the persistent lifetime totals (these survive deletion); fall
    // back to the live job list until the usage summary loads or if it fails.
    const total = lifetime?.audits ?? allJobs.length;
    const totalFindings =
      lifetime?.findings ?? allJobs.reduce((sum, j) => sum + (j.findingsCount ?? 0), 0);
    const last30 =
      lifetime?.auditsLast30d ?? allJobs.filter((j) => j.createdAt >= thirtyDaysAgo).length;
    return { total, totalFindings, running, sites, last30 };
  }, [allJobs, lifetime]);

  /* gates */
  if (auth.status !== "authenticated") return <DashboardSkeleton />;
  if (jobs.status === "loading") return <DashboardSkeleton />;

  const { user, entitlements } = auth;
  // Prefer the Settings display name; fall back to the email-derived name.
  const firstName = displayName || (user.email ? humanizeName(user.email) : null);
  const features = entitlements.features;
  const hasRuns = allJobs.length > 0;
  const credits = entitlements.credits.balance;
  const monthlyGrant = entitlements.credits.monthlyGrant;
  // Treat 0 credits and < 20% of the monthly grant as "low" so the
  // CTA shows up before users hit the wall.
  const isOutOfCredits = credits === 0;
  const isLowCredits = !isOutOfCredits && monthlyGrant > 0 && credits / monthlyGrant < 0.2;
  const tip = isOutOfCredits
    ? {
        body: "You're out of credits. Add a top-up or upgrade to keep auditing — your existing audits stay accessible.",
        link: "Add credits",
        href: "/account#topup",
      }
    : isLowCredits
      ? {
          body: `You're running low on credits (${credits} of ${monthlyGrant} left). Top up to keep your audits flowing this month.`,
          link: "Add credits",
          href: "/account#topup",
        }
      : pickTip(allJobs.length);

  return (
    <div className="dash-page">
      {/* First-sign-in welcome — set expectations so the user isn't
          dropped into the dashboard wondering what just happened. The
          state lives in localStorage so dismissal is permanent per
          device. Only renders when the login flow has flagged a
          fresh sign-in; once dismissed it sets a `completed` flag
          that prevents re-showing on future logins. */}
      {showWelcome ? (
        <section
          className="dash-welcome"
          role="region"
          aria-labelledby="dash-welcome-heading"
        >
          <div className="dash-welcome-icon" aria-hidden="true">
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </div>
          <div className="dash-welcome-body">
            <h2 id="dash-welcome-heading">Welcome to Snap Site &mdash; you&rsquo;re all set.</h2>
            <p>
              {/* Read the actual balance — was hardcoded "5" before,
                  which would have silently become a lie the moment
                  the Free monthly grant ever changed. */}
              Your account is ready and your{" "}
              <strong>
                {credits} {credits === 1 ? "credit is" : "credits are"}
              </strong>{" "}
              loaded. Here&rsquo;s what happens next:
            </p>
            <ol className="dash-welcome-steps">
              <li>
                <span className="dash-welcome-step-num">1</span>
                Paste a URL below to start your first audit.
              </li>
              <li>
                <span className="dash-welcome-step-num">2</span>
                We capture the page, run usability heuristics, and check
                accessibility &mdash; typically in about a minute.
              </li>
              <li>
                <span className="dash-welcome-step-num">3</span>
                Findings appear pinned on your screenshot, colour-coded by
                severity, with plain-language notes you can share.
              </li>
            </ol>
          </div>
          <button
            type="button"
            className="dash-welcome-dismiss"
            onClick={dismissWelcome}
            aria-label="Dismiss welcome message"
          >
            Got it
          </button>
        </section>
      ) : null}

      {/* Header */}
      <div className="dash-header">
        <div>
          <h1 className="dash-greeting">
            {greeting(firstName)}
          </h1>
          <p className="dash-subline">
            {hasRuns
              ? "Pick up where you left off, or start something new."
              : "Run your first UX audit in under a minute."}
          </p>
        </div>
        {/* aria-live announces to screen readers when a run starts or
            finishes — without this, the count change is silent. The
            badge itself is visual; the polite live region wraps it so
            assistive tech narrates "1 audit running" / "2 audits
            running" as the count flips. */}
        {hasRuns && stats.running > 0 ? (
          <span className="dash-running-badge" role="status" aria-live="polite">
            <span className="dash-running-dot" aria-hidden="true" />
            {stats.running} audit{stats.running === 1 ? "" : "s"} running
          </span>
        ) : null}
      </div>

      {/* Two-column layout */}
      <div className="dash-columns">
        {/* Main — left.
            Empty-state order: process explainer up top, then the
            action card. The concrete example moved to the aside
            (right) so this column stays focused on "understand → act"
            without stacking three heavy sections vertically.
            Returning users skip the orientation entirely. */}
        <div className="dash-main">
          {/* Compact "how it works" hint. Previously a boxed 3-card
              strip with chevrons between steps — useful orientation
              but heavy chrome for a tool that's about to show the
              audit creator right below. Slimmed to a single line of
              micro-copy so it gives the same context with a fraction
              of the visual weight. The creator below makes steps 1
              and 2 self-evident; step 3 is what happens after. */}
          {!hasRuns && (
            <p className="dash-hiw-hint">
              <span className="dash-hiw-hint-label">How it works:</span>
              <span className="dash-hiw-hint-step">1. Paste a URL</span>
              <ChevronRightIcon />
              <span className="dash-hiw-hint-step">2. Pick your settings</span>
              <ChevronRightIcon />
              <span className="dash-hiw-hint-step">3. Review findings</span>
            </p>
          )}

          <ReviewCreator
            token={auth.token}
            features={features}
            credits={entitlements.credits.balance}
            monthlyGrant={entitlements.credits.monthlyGrant}
            recentJobUrls={recentJobUrls}
          />

          {/* Error banners */}
          {jobs.status === "error" && (
            <div className="dash-error-banner">
              <span>Couldn&apos;t load your history &mdash; </span>
              <button type="button" className="dash-retry-btn" onClick={retryLoad}>try again</button>
            </div>
          )}

          {deleteError && (
            <div className="dash-error-banner">
              <span>{deleteError}</span>
              <button type="button" className="dash-retry-btn" onClick={() => setDeleteError(null)}>dismiss</button>
            </div>
          )}

          {/* ---- RETURNING USER: Recent audits ---- */}
          {hasRuns && (
            <section className="rr rr-card">
              <div className="rr-header">
                <h2 className="rr-title">Recent audits</h2>
                <Link href="/projects" className="rr-view-all rr-view-all-inline">
                  View all audits →
                </Link>
              </div>

              <ul className="rr-list">
                {recentJobs.slice(0, 6).map((job) => (
                  <ReviewRow
                    key={job.id}
                    job={job}
                    onDelete={() => setPendingDelete(job)}
                  />
                ))}
              </ul>
            </section>
          )}
        </div>

        {/* Aside — right */}
        <aside className="dash-aside">
          {/* Returning users: trimmed stats summary. The original card
              showed four numbers — Audits, Findings, Sites audited,
              Credits left. Sites audited tracks Audits 1:1 for most
              solo users, and Credits left is already shown in the
              sidebar bar plus the New audit card's badge. Both
              cut. Audits + Findings are the cumulative work metrics
              that don't appear anywhere else. */}
          {hasRuns && (
            <section className="dash-stats">
              <h2 className="dash-stats-title">Your activity</h2>
              <div className="dash-stats-grid">
                <div className="dash-stat dash-stat-audits">
                  <span className="dash-stat-value">{stats.total}</span>
                  <span className="dash-stat-label">Audits</span>
                </div>
                <div className="dash-stat dash-stat-findings">
                  <span className="dash-stat-value">{stats.totalFindings}</span>
                  <span className="dash-stat-label">Findings</span>
                </div>
              </div>
              {/* Velocity caption — contextualizes the two lifetime
                  totals with recent activity so the card doesn't feel
                  like a static accomplishments display. Hidden when
                  there's nothing to report; a zero would land as a
                  scold ("no audits lately") which the calmer empty-
                  state copy elsewhere already addresses. */}
              {stats.last30 > 0 ? (
                <p className="dash-stats-foot">
                  {stats.last30} in the last 30 days
                </p>
              ) : null}
            </section>
          )}

          {/* New users: combined "Getting started" card.
              Previously three separate aside blocks competed for the
              new user's attention (example preview, "What you get"
              4-bullet feature list, generic onboarding Tip). Merged
              into one card with the concrete proof up top (example
              preview) and the contextual tip beneath, so the aside
              speaks once instead of three times. The "What you get"
              feature bullets were dropped entirely — that's marketing
              copy meant for someone deciding whether to sign up, not
              for someone already inside the tool about to use it. */}
          {!hasRuns && (
            <section className="dash-getstarted">
              <h2 className="dash-getstarted-title">Getting started</h2>
              <AppToMarketingLink
                href="/#demos"
                className="dash-getstarted-example"
                showIcon={false}
              >
                <div className="dash-example-preview">
                  <ExamplePreviewIllustration />
                </div>
                <div className="dash-example-info">
                  <strong>See an example</strong>
                  <span className="dash-example-meta">
                    <span>Desktop + Mobile</span>
                    <span aria-hidden="true">·</span>
                    <span>12 findings</span>
                  </span>
                  <span className="dash-example-cta">Explore →</span>
                </div>
              </AppToMarketingLink>
              <div className="dash-getstarted-tip">
                <div className="tip-header">
                  <LightbulbIcon />
                  <strong>Tip</strong>
                </div>
                <p className="tip-body">{tip.body}</p>
                <AppToMarketingLink href={tip.href} className="tip-link" showIcon={false}>
                  {tip.link} &rarr;
                </AppToMarketingLink>
              </div>
            </section>
          )}

          {/* Standalone tip for returning users — early-stage
              onboarding (audits 1–3) or low/out-of-credit states. The
              new-user case is already handled by the combined
              "Getting started" card above; this branch covers users
              who have audits but still benefit from contextual
              nudges. */}
          {hasRuns && (allJobs.length < 3 || isLowCredits || isOutOfCredits) && (
            <div className="tip">
              <div className="tip-header">
                <LightbulbIcon />
                <strong>Tip</strong>
              </div>
              <p className="tip-body">{tip.body}</p>
              <AppToMarketingLink href={tip.href} className="tip-link" showIcon={false}>
                {tip.link} &rarr;
              </AppToMarketingLink>
            </div>
          )}
        </aside>
      </div>

      {pendingDelete && (
        <DeleteDialog
          job={pendingDelete}
          deleting={deleting}
          onCancel={() => setPendingDelete(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Review Creator — the main action card                              */
/* ------------------------------------------------------------------ */

function ReviewCreator({
  token,
  features,
  credits,
  monthlyGrant,
  recentJobUrls,
}: {
  token: string;
  features: { mobile: boolean; bothViewports: boolean; aiInteractions: boolean; maxPagesPerRun: number };
  credits: number;
  monthlyGrant: number;
  /** Recents derived from the real job history (newest first). */
  recentJobUrls?: string[];
}) {
  const router = useRouter();

  // Input source — URL audit (default) or screenshot upload. Mutually
  // exclusive at the top of the card. The two paths have genuinely
  // different inputs and settings, so this is tabs, not a chip inside
  // a settings panel where it would have implied a tweak rather than a
  // fundamental fork.
  const [source, setSource] = useState<InputSource>("url");

  // URL-audit state. Defaults match the product's opinionated baseline
  // so a first-time user can click "Start audit" without ever opening
  // the settings panel. `mode` controls whether the run produces an AI
  // audit (with findings) or just screenshots — the latter is a power-
  // user variant exposed as the first row in the settings panel.
  const [url, setUrl] = useState("");
  const [mode, setMode] = useState<RunMode>("audit");
  const [viewport, setViewport] = useState<ViewportOption>("desktop");
  const [pages, setPages] = useState(3);
  const [aiNote, setAiNote] = useState("");
  const [aiOpen, setAiOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [recents, setRecents] = useState<string[]>([]);
  // Real job history beats the per-browser localStorage trail; the
  // stored list is just the fallback while jobs are still loading.
  const displayRecents =
    recentJobUrls && recentJobUrls.length > 0 ? recentJobUrls : recents;

  // Image-audit state.
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const urlInputRef = useRef<HTMLInputElement>(null);

  // "Site card" evidence line under the URL bar — same shared component as
  // the marketing hero form and /audit/new (components/site-preview.tsx):
  // the page's real title, an empty-shell warning, a cross-host redirect, or
  // "did you mean airbnb.com?". Warn-level verdicts arm a one-click "are you
  // sure?" on submit; a fast click holds on "Checking the address…" until
  // the same check resolves. Never hard-blocks.
  const [confirmArmed, setConfirmArmed] = useState(false);
  const confirmArmedAt = useRef(0);
  const [checkingUrl, setCheckingUrl] = useState(false);
  const disarmConfirm = useCallback(() => setConfirmArmed(false), []);
  const { preview: sitePreview, ensure: ensureSitePreview } = useSitePreflight(
    source === "url" ? url : "",
    "dashboard",
    disarmConfirm
  );

  /* Load recents */
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(RECENT_URLS_KEY);
      if (raw) setRecents(JSON.parse(raw) as string[]);
    } catch { /* ignore */ }
  }, []);

  /* Apply the user's saved audit defaults from /settings. Same logic
     as the standalone /audit/new page — both creators read from a
     single source of truth so a user only has to express their
     defaults once. Feature gates (next effect) clamp invalid values
     like "mobile" for a Free user. */
  useEffect(() => {
    const defaults = loadAuditDefaults();
    if (!defaults) return;
    setPages(defaults.defaultPages);
    setAiOpen(defaults.aiGuidance);
    setViewport(defaults.devicePreference);
  }, []);

  /* Manage the blob URL lifecycle for the upload preview. */
  useEffect(() => {
    if (!imageFile) { setImagePreview(null); return; }
    const blob = URL.createObjectURL(imageFile);
    setImagePreview(blob);
    return () => URL.revokeObjectURL(blob);
  }, [imageFile]);

  const canMobile = features.mobile;
  const canBoth = features.bothViewports;
  const canAi = features.aiInteractions;
  const maxPages = features.maxPagesPerRun;

  /* If the user is on a plan that drops mobile partway through a session,
     bring the choice back to something allowed. */
  useEffect(() => {
    if (viewport === "mobile" && !canMobile) setViewport("desktop");
    if (viewport === "both" && !canBoth) setViewport("desktop");
  }, [viewport, canMobile, canBoth]);

  /* Live cost preview. Image uploads are a flat 1 credit; URL runs scale
     by viewport count × pages, plus the +5 AI-assistance surcharge. AI
     assistance works for both audits and screenshots (it directs which
     pages/states to capture), so it isn't gated to audit mode. */
  const effectivePages = Math.min(pages, maxPages);
  const creditCost = useMemo(() => {
    if (source === "image") return 1;
    const vpCount = viewport === "both" ? 2 : 1;
    const base = vpCount * effectivePages;
    const aiBonus = aiNote.trim() && canAi ? 5 : 0;
    return base + aiBonus;
  }, [source, viewport, effectivePages, aiNote, canAi]);
  const enoughCredits = credits >= creditCost;

  /* The calm sentence describing what a URL run will do. Cost is
     intentionally NOT in this sentence — it lives on the CTA button,
     paired with the commitment moment. Balance lives in the top-right
     badge. One place for each number; no echoing. */
  const settingsSummary = useMemo(() => {
    const pagesText = effectivePages === 1 ? "Just this page" : `${effectivePages} pages`;
    const vpText = viewport === "both" ? "Desktop + Mobile" : viewport === "mobile" ? "Mobile" : "Desktop";
    const modeText = mode === "snapshot" ? " · Screenshots only" : "";
    const aiText = aiNote.trim() && canAi ? " · AI assistance" : "";
    return `${pagesText} · ${vpText}${modeText}${aiText}`;
  }, [mode, viewport, effectivePages, aiNote, canAi]);

  /* Image validation + pickers */
  function validateImageFile(file: File): string | null {
    if (!(IMAGE_ACCEPTED_TYPES as readonly string[]).includes(file.type)) {
      return "Only PNG, JPEG, or WebP files are supported.";
    }
    if (file.size === 0) return "That file looks empty.";
    if (file.size > IMAGE_MAX_BYTES) {
      return `Image is too large. Max ${(IMAGE_MAX_BYTES / 1_000_000).toFixed(1)} MB.`;
    }
    return null;
  }
  function pickImage(file: File | null | undefined) {
    setSubmitError(null);
    if (!file) { setImageFile(null); return; }
    const v = validateImageFile(file);
    if (v) { setSubmitError(v); setImageFile(null); return; }
    setImageFile(file);
  }
  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) pickImage(file);
  }

  /* Submit — branches on input source. Both routes land on /audit/[id]. */
  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting || checkingUrl) return;

    if (source === "image") {
      if (!imageFile) {
        setSubmitError("Choose a screenshot to upload.");
        return;
      }
      if (!enoughCredits) {
        setSubmitError(`This upload needs ${creditCost} credit${creditCost === 1 ? "" : "s"} but you have ${credits}.`);
        return;
      }
      setSubmitting(true);
      setSubmitError(null);
      try {
        const result = await startImageAudit(token, { file: imageFile, clientId: readClientId() });
        router.push(`/audit/${result.jobId}`);
      } catch (err) {
        const msg = err instanceof ApiError
          ? err.message
          : err instanceof Error ? err.message : "";
        setSubmitError(msg || "Could not start the image audit. Your credits were not used — try again.");
        setSubmitting(false);
      }
      return;
    }

    // URL flow.
    const trimmed = url.trim();
    if (!trimmed) {
      setSubmitError("Paste a URL to audit.");
      urlInputRef.current?.focus();
      return;
    }
    // Self-audit guard — checked before credits so even out-of-credits users
    // get the message, never an API call against our own site.
    if (isOurOwnDomain(trimmed)) {
      setSubmitError(SELF_AUDIT_MESSAGE);
      return;
    }
    // Structural URL sanity before credits or any API call — same rules the
    // backend enforces pre-charge (see backend/src/url-validation.ts), run
    // locally so a typo ("easy mails.cc") answers instantly.
    const validUrl = validateAuditUrl(trimmed);
    if (!validUrl.ok) {
      setSubmitError(
        validUrl.suggestion
          ? `${validUrl.message} Did you mean ${validUrl.suggestion}?`
          : validUrl.message
      );
      return;
    }
    // Double-click guard: an armed confirm ignores clicks for a beat, so
    // "click-click" can't blow through a question that hadn't even painted.
    if (confirmArmed && Date.now() - confirmArmedAt.current < 600) return;

    // "Are you sure?" — one interception when the verdict for THE HOST BEING
    // SUBMITTED is warn-level. If the typing preview hasn't resolved yet
    // (fast paste-and-click), hold the click and resolve it now. Still
    // fail-open: a timed-out or failed check proceeds like today.
    if (!confirmArmed) {
      let verdict =
        sitePreview.status === "ready" &&
        sitePreview.hostname === validUrl.hostname
          ? sitePreview
          : null;
      if (!verdict) {
        setCheckingUrl(true);
        const resolved = await ensureSitePreview(trimmed);
        setCheckingUrl(false);
        if (
          resolved.status === "ready" &&
          resolved.hostname === validUrl.hostname
        ) {
          verdict = resolved;
        }
      }
      if (verdict && isWarnPreview(verdict)) {
        setConfirmArmed(true);
        confirmArmedAt.current = Date.now();
        return;
      }
    }
    if (!enoughCredits) {
      setSubmitError(`This run needs ${creditCost} credit${creditCost === 1 ? "" : "s"} but you have ${credits}.`);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await startAnalyze(token, {
        url: trimmed,
        runMode: mode,
        viewports: viewportsForOption(viewport),
        // Findings + notes only make sense for the audit mode. Snapshot
        // mode is "capture only" — backend skips heuristics if false.
        heuristicsAudit: mode === "audit",
        notes: mode === "audit",
        highlights: false,
        maxPages: effectivePages,
        clientId: readClientId(),
        // AI assistance directs which pages/states to capture, so it
        // applies to screenshots-only runs too — not just audits.
        ...(aiNote.trim() && canAi ? { aiNote: aiNote.trim() } : {}),
      });
      pushRecentUrl(trimmed);
      router.push(`/audit/${result.jobId}`);
    } catch (err) {
      const msg = err instanceof ApiError
        ? err.message
        : err instanceof Error
          ? err.message
          : "";
      const lower = msg.toLowerCase();
      if (lower.includes("credit") || lower.includes("quota") || lower.includes("limit")) {
        setSubmitError("This audit needs more credits than you have. Capture fewer pages or add credits.");
      } else if (lower.includes("url") || lower.includes("dns") || lower.includes("unreachable")) {
        setSubmitError("We couldn't reach this page. Check the URL or try a public link.");
      } else {
        setSubmitError(msg || "Something went wrong. Your credits were not used — try again.");
      }
      setSubmitting(false);
    }
  }

  const isOutOfCredits = credits === 0;
  const isLowCredits = !isOutOfCredits && monthlyGrant > 0 && credits / monthlyGrant < 0.2;

  /* CTA copy: cost is suffixed onto the action verb. Pairs the cost
     with the commitment moment — the only place the user will look
     for "how much will this cost me right now?"

     The verb is consistent across URL and Upload tabs: both are the
     user starting a review. The snapshot sub-mode of the URL tab is
     the one exception — it's not technically a review, just a capture,
     so the verb changes to match what the user gets. */
  const costSuffix = ` · ${creditCost} credit${creditCost === 1 ? "" : "s"}`;
  const ctaActionLabel =
    source === "url" && mode === "snapshot" ? "Take screenshots" : "Start audit";
  // Submitting copy names what's starting so the user knows the
  // process kicked off. "Starting…" alone left it ambiguous whether
  // the page-load was in flight or the audit job itself.
  const submittingLabel =
    source === "image"
      ? "Starting upload…"
      : mode === "snapshot"
        ? "Capturing…"
        : "Starting audit…";
  const ctaLabel = submitting
    ? submittingLabel
    : checkingUrl
      ? "Checking the address…"
      : confirmArmed
        ? `Audit anyway${costSuffix}`
        : ctaActionLabel + costSuffix;

  return (
    <div className="rc">
      <div className="rc-head">
        <h2 className="rc-title">New audit</h2>
        {isOutOfCredits ? (
          <Link href="/account#topup" className="rc-credit-cta" aria-label="Add credits to start an audit">
            Add credits to start →
          </Link>
        ) : isLowCredits ? (
          <Link href="/account#topup" className="rc-credit-cta" aria-label={`Only ${credits} credits left — add more`}>
            {credits} left — add more →
          </Link>
        ) : (
          <span className="rc-credit-badge" aria-label={`${credits} credits available`}>
            {credits} credit{credits !== 1 ? "s" : ""} available
          </span>
        )}
      </div>

      {/* Input-source tabs. URL is the dominant path; image upload is
          a secondary one. Equal-width tabs make the fork explicit
          without competing with the URL bar's primary affordance. */}
      <div className="rc-tabs" role="tablist" aria-label="Audit input source">
        <button
          type="button"
          role="tab"
          aria-selected={source === "url"}
          className={`rc-tab${source === "url" ? " active" : ""}`}
          onClick={() => { setSource("url"); setSubmitError(null); }}
        >
          From URL
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={source === "image"}
          className={`rc-tab${source === "image" ? " active" : ""}`}
          onClick={() => { setSource("image"); setSubmitError(null); }}
        >
          Upload image
        </button>
      </div>

      {source === "url" ? (
        // The whole URL flow is one <form> so the CTA at the bottom
        // still submits the URL bar above. The button no longer lives
        // INSIDE the URL bar — it sits in a CTA row at the foot of the
        // card, matching the Upload tab's pattern exactly. Same verb,
        // same row, same position.
        <form onSubmit={handleSubmit}>
          <div className="rc-bar">
            <LinkIcon />
            <input
              ref={urlInputRef}
              className="rc-bar-input"
              type="text"
              inputMode="url"
              aria-label="Website URL"
              placeholder="Paste a URL to audit"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                if (submitError && e.target.value.trim()) setSubmitError(null);
                if (confirmArmed) setConfirmArmed(false);
              }}
              autoComplete="off"
              spellCheck={false}
              autoFocus
              aria-invalid={submitError ? true : undefined}
              disabled={submitting || checkingUrl}
            />
          </div>
          {submitError ? (
            <p className="rc-bar-error" role="alert">{submitError}</p>
          ) : null}

          {/* Site-card evidence line — shared component + styles with the
              hero form and /audit/new so the cards never drift apart. */}
          {!submitError && sitePreview.status === "ready" && (
            <p
              className={`lp-hero-form-preview${
                isWarnPreview(sitePreview) || sitePreview.verdict === "noexist"
                  ? " is-warn"
                  : ""
              }`}
              aria-live="polite"
            >
              <SiteFavicon hostname={sitePreview.hostname} />
              <span>
                <SitePreviewMessage
                  preview={sitePreview}
                  confirmArmed={confirmArmed}
                  onSwap={(host) => {
                    setUrl(host);
                    setConfirmArmed(false);
                  }}
                />
              </span>
            </p>
          )}

          {/* Recent URLs — chip labels include the path when present so
              acme.com/landing and acme.com/pricing are distinguishable.
              Hostname-only labels collapsed every audit of the same
              site into the same chip, which made the picker useless
              for users who audit multiple pages per site. */}
          {displayRecents.length > 0 && (
            <div className="rc-recents">
              <span className="rc-recents-label">Recent:</span>
              {displayRecents.slice(0, 4).map((r) => (
                <button
                  key={r}
                  type="button"
                  className="rc-recent-chip"
                  onClick={() => setUrl(r)}
                  title={r}
                >
                  {chipLabelFor(r)}
                </button>
              ))}
            </div>
          )}

          {/* Calm settings line. Cost moved off this line (it's on the
              CTA); balance lives in the top-right badge. One number per
              place — answers "credits aren't clear". */}
          <div className="rc-runline">
            <span className="rc-runline-text">{settingsSummary}</span>
            {!submitting ? (
              <button
                type="button"
                className="rc-runline-toggle"
                onClick={() => setSettingsOpen((v) => !v)}
                aria-expanded={settingsOpen}
                aria-controls="rc-runpanel"
              >
                {/* Explicit verb-noun copy — "Change" alone left users
                    asking "change what?" Underlined link styling (see
                    .rc-runline-toggle) signals clickable without competing
                    with the solid CTA. */}
                {settingsOpen ? "Hide settings" : "Show settings"}
              </button>
            ) : null}
          </div>

          {!enoughCredits ? (
            <p className="rc-runline-warn" role="status">
              Short by {creditCost - credits} credit{creditCost - credits !== 1 ? "s" : ""}.{" "}
              <Link href="/account#topup">Add credits</Link>
              {" or "}
              <AppToMarketingLink href="/pricing#plans">upgrade</AppToMarketingLink>.
            </p>
          ) : null}

          {settingsOpen ? (
            <div id="rc-runpanel" className="rc-runpanel">
              {/* Output is the most fundamental choice — it dictates
                  what the run produces (findings vs. just screenshots).
                  It leads the panel. Same pill-toggle style as Scope,
                  Device, Pages — uniform visual all the way down. */}
              <div className="rc-settings-row">
                <SettingLabel icon={<OutputGlyph />}>What to produce</SettingLabel>
                <div className="rc-toggle">
                  <button
                    type="button"
                    className={`rc-toggle-btn${mode === "audit" ? " active" : ""}`}
                    aria-pressed={mode === "audit"}
                    onClick={() => setMode("audit")}
                  >
                    Full audit
                  </button>
                  <button
                    type="button"
                    className={`rc-toggle-btn${mode === "snapshot" ? " active" : ""}`}
                    aria-pressed={mode === "snapshot"}
                    onClick={() => setMode("snapshot")}
                  >
                    Screenshots only
                  </button>
                </div>
              </div>

              <div className="rc-settings-row">
                <SettingLabel icon={<DeviceGlyph />}>Which device</SettingLabel>
                <div className="rc-toggle rc-toggle-device">
                  <button type="button" className={`rc-toggle-btn${viewport === "desktop" ? " active" : ""}`} aria-pressed={viewport === "desktop"} onClick={() => setViewport("desktop")}>
                    <DesktopIcon /> Desktop
                  </button>
                  {/* Locked plan-gated chips are interactive now: a
                      silent dead pill teaches the user nothing about
                      why they can't pick it. On click, locked chips
                      open the in-app upgrade ladder (/account#upgrade)
                      in a new tab so the in-progress audit session
                      stays alive. Hover/focus reveals a tooltip. */}
                  <button
                    type="button"
                    className={`rc-toggle-btn${viewport === "mobile" ? " active" : ""}${!canMobile ? " rc-locked" : ""}`}
                    aria-pressed={viewport === "mobile"}
                    aria-disabled={!canMobile}
                    title={canMobile ? undefined : "Mobile audits are on the Pro plan. Click to see pricing."}
                    onClick={() => canMobile ? setViewport("mobile") : openUpgradeInNewTab()}
                  >
                    <MobileIcon /> Mobile {!canMobile && <span className="rc-pro">Pro</span>}
                  </button>
                  <button
                    type="button"
                    className={`rc-toggle-btn${viewport === "both" ? " active" : ""}${!canBoth ? " rc-locked" : ""}`}
                    aria-pressed={viewport === "both"}
                    aria-disabled={!canBoth}
                    title={canBoth ? undefined : "Auditing Desktop + Mobile in one run is on the Pro plan. Click to see pricing."}
                    onClick={() => canBoth ? setViewport("both") : openUpgradeInNewTab()}
                  >
                    <BothIcon /> Both {!canBoth && <span className="rc-pro">Pro</span>}
                  </button>
                </div>
              </div>

              {/* One page-count control — "Just this page" is the
                  1-page case in words; higher counts crawl from the
                  entered URL. Mirrors /audit/new. The old scope toggle
                  was UI-only (the backend only ever got the count). */}
              <div className="rc-settings-row">
                <SettingLabel icon={<PagesGlyph />}>Pages to capture</SettingLabel>
                <div className="rc-pages-control">
                  <div className="rc-toggle rc-toggle-pages">
                    {PAGE_OPTIONS.map((n) => {
                      const locked = n > maxPages;
                      const isSingle = n === 1;
                      return (
                        <button
                          key={n}
                          type="button"
                          className={`rc-toggle-btn${isSingle ? "" : " rc-toggle-num"}${pages === n ? " active" : ""}${locked ? " rc-locked" : ""}`}
                          aria-pressed={pages === n}
                          aria-disabled={locked}
                          aria-label={isSingle ? "Just this page" : `Up to ${n} pages`}
                          title={locked ? `Auditing ${n} pages per run is on the Pro plan. Click to see pricing.` : undefined}
                          onClick={() => locked ? openUpgradeInNewTab() : setPages(n)}
                        >
                          {isSingle ? "Just this page" : n}
                          {locked && <span className="rc-pro">Pro</span>}
                        </button>
                      );
                    })}
                  </div>
                  <p className="rc-pages-hint">Higher counts crawl from your URL and capture more pages.</p>
                </div>
              </div>

              {/* AI assistance — optional accordion at the bottom (extra
                  credits). Available for both audits AND screenshots: it
                  directs WHICH pages or WHICH state to capture, which is
                  just as useful for a screenshot run as an audit. */}
              <div className={`rc-accordion${aiOpen ? " is-open" : ""}`}>
                {/* Locked accordion was disabled — silent. Now it acts
                    as an upgrade nudge: click opens pricing in a new
                    tab. Consistent with the locked Mobile/Both/Pages
                    chips above. */}
                <button
                  type="button"
                  className={`rc-accordion-head${!canAi ? " is-locked" : ""}`}
                  aria-expanded={canAi ? aiOpen : false}
                  aria-controls="rc-ai-instructions"
                  aria-disabled={!canAi}
                  onClick={() => canAi ? setAiOpen((v) => !v) : openUpgradeInNewTab()}
                >
                  <span className="rc-accordion-chev" aria-hidden="true">
                    <ChevronDownIcon />
                  </span>
                  {/* Vocabulary matches the marketing pages ("AI
                      guidance" on home, pricing, blog). Naming the
                      same feature differently here ("Pre-audit setup")
                      was a learnability tax — users had to map the
                      two terms to one feature on their own. */}
                  <span className="rc-accordion-title">AI assistance</span>
                  <span className="rc-accordion-meta">optional · +5 credits</span>
                  {!canAi && <span className="rc-pro">Pro</span>}
                </button>
                {aiOpen && canAi ? (
                  <div className="rc-accordion-body">
                    <p className="rc-ai-hint">
                      Tell the AI which pages to capture (e.g. &ldquo;pricing, checkout, FAQ&rdquo;) or which state to reach first (e.g. &ldquo;open the menu, switch to dark mode&rdquo;). Works for audits and screenshots.
                    </p>
                    <textarea
                      id="rc-ai-instructions"
                      className="rc-ai-textarea"
                      aria-label="AI assistance instructions"
                      rows={2}
                      maxLength={100}
                      spellCheck
                      placeholder="Example: capture pricing & checkout — or open the mobile menu first."
                      value={aiNote}
                      onChange={(e) => setAiNote(e.target.value.slice(0, 100))}
                    />
                    <div className="rc-ai-row">
                      <div className="rc-ai-pills">
                        {AI_EXAMPLES.map((ex) => (
                          <button key={ex} type="button" className="rc-ai-pill" onClick={() => setAiNote(ex)}>{ex}</button>
                        ))}
                      </div>
                      <span className="rc-ai-count">{aiNote.length}/100</span>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          {/* CTA row at the foot of the card — same shape, position,
              and class as the Upload tab's row below. The button is
              full-style primary regardless of which tab is active. */}
          <div className="rc-cta-row">
            <button
              type="submit"
              className="rc-bar-btn"
              disabled={submitting || checkingUrl}
            >
              {ctaLabel}
            </button>
          </div>
        </form>
      ) : (
        // Upload-image tab. Image audits have a fixed shape (1 page, 1
        // viewport, 1 credit) so the settings panel isn't needed here.
        <form onSubmit={handleSubmit} className="rc-upload-form">
          <div
            className={`nr-dropzone${dragging ? " dragging" : ""}${imageFile ? " has-file" : ""}`}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            onClick={() => { if (!imageFile) fileInputRef.current?.click(); }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={IMAGE_ACCEPT_ATTR}
              className="nr-dropzone-input"
              aria-label="Choose a screenshot to upload"
              onChange={(e) => pickImage(e.target.files?.[0])}
            />
            {imageFile && imagePreview ? (
              <div className="nr-dropzone-preview">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={imagePreview} alt="Uploaded screenshot preview" className="nr-dropzone-thumb" />
                <div className="nr-dropzone-fileinfo">
                  <p className="nr-dropzone-filename">{imageFile.name}</p>
                  <p className="nr-dropzone-filemeta">
                    {(imageFile.size / 1_000_000).toFixed(2)} MB · {imageFile.type.split("/")[1].toUpperCase()}
                  </p>
                  <div className="nr-dropzone-actions">
                    <button type="button" className="nr-dropzone-replace" onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}>Replace</button>
                    <button type="button" className="nr-dropzone-remove" onClick={(e) => { e.stopPropagation(); setImageFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}>Remove</button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="nr-dropzone-empty">
                <UploadCloudIcon />
                <p className="nr-dropzone-title">Drop a screenshot here, or click to choose</p>
                <p className="nr-dropzone-hint">
                  PNG, JPEG, or WebP. Up to {(IMAGE_MAX_BYTES / 1_000_000).toFixed(1)} MB.
                </p>
              </div>
            )}
          </div>

          {submitError ? (
            <p className="rc-bar-error" role="alert">{submitError}</p>
          ) : null}

          {!enoughCredits ? (
            <p className="rc-runline-warn" role="status">
              Short by {creditCost - credits} credit{creditCost - credits !== 1 ? "s" : ""}.{" "}
              <Link href="/account#topup">Add credits</Link>
              {" or "}
              <AppToMarketingLink href="/pricing#plans">upgrade</AppToMarketingLink>.
            </p>
          ) : null}

          {/* Disabled only during in-flight submit. !imageFile and
              !enoughCredits no longer gate visual state — both buttons
              read as solid-teal primary at rest, matching the URL tab.
              handleSubmit validates and surfaces inline errors on click. */}
          <div className="rc-cta-row">
            <button type="submit" className="rc-bar-btn" disabled={submitting}>
              {ctaLabel}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Review row (list item)                                             */
/* ------------------------------------------------------------------ */

function ReviewRow({ job, onDelete }: { job: JobSummary; onDelete: () => void }) {
  const router = useRouter();
  const [copied, setCopied] = useState(false);
  const [reRunning, setReRunning] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { token } = useAppAuth() as { token: string };

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent) => { if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setMenuOpen(false); };
    window.addEventListener("mousedown", close);
    window.addEventListener("keydown", esc);
    return () => {
      window.removeEventListener("mousedown", close);
      window.removeEventListener("keydown", esc);
    };
  }, [menuOpen]);

  async function reAudit() {
    setReRunning(true);
    try {
      const result = await startAnalyze(token, {
        url: job.sourceUrl,
        runMode: job.runMode ?? "audit",
        viewports: job.viewports ?? ["desktop"],
        heuristicsAudit: (job.runMode ?? "audit") === "audit",
        notes: true, highlights: false,
        maxPages: job.pageCount || 5,
        clientId: readClientId(),
      });
      router.push(`/audit/${result.jobId}`);
    } catch { setReRunning(false); }
  }

  const f = findingsLabel(job);
  const viewports = job.viewports ?? ["desktop"];
  // Full words — the previous "D + M" / "5p" shorthand saved a few
  // pixels but made the column unreadable for anyone who didn't
  // already know the convention.
  const viewportLabel = viewports.length > 1
    ? "Desktop + Mobile"
    : viewports[0] === "mobile" ? "Mobile" : "Desktop";
  const pageCount = job.pageCount || 1;
  const pageCountLabel = `${pageCount} ${pageCount === 1 ? "page" : "pages"}`;
  // Image-upload audits store the uploaded file's blob URL as sourceUrl; its
  // hostname is the meaningless Vercel-storage bucket id, so show a clean
  // label instead of the raw blob host.
  const isImageAudit = job.runMode === "imageAudit";
  const displayName = job.siteName || (isImageAudit ? "Uploaded screenshot" : hostnameFor(job.sourceUrl));
  const domain = isImageAudit ? "Uploaded image" : hostnameFor(job.sourceUrl);
  const typeLabel = modeLabel(job.runMode);

  return (
    <li className="rr-row">
      <Link
        href={`/audit/${job.id}`}
        className="rr-row-link"
        aria-label={`Open ${displayName} audit`}
      >
        <div className="rr-row-thumb">
          {job.thumbnailUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={job.thumbnailUrl} alt="" />
          ) : (
            <div className="rr-row-thumb-empty"><LayoutIcon /></div>
          )}
          {job.status === "running" && <span className="rr-row-status-dot rr-status-running" aria-label="Running" data-tip="Running" />}
          {job.status === "error" && <span className="rr-row-status-dot rr-status-error" aria-label="Failed" data-tip="Failed" />}
        </div>
        <div className="rr-row-info">
          <p className="rr-row-name">{displayName}</p>
          <p className="rr-row-domain">{domain}</p>
          {/* Provenance — flags audits created in the Figma plugin. */}
          {job.origin === "plugin" ? (
            <FigmaOriginChip className="rr-row-figma-chip" />
          ) : null}
        </div>
      </Link>
      <span className="rr-row-type">{typeLabel}</span>
      <span className="rr-row-config">
        <span>{viewportLabel}</span>
        <span className="rr-row-sep" aria-hidden="true" />
        <span>{pageCountLabel}</span>
      </span>
      <span
        className={`rr-row-findings rr-tone-${f.tone}`}
        title={f.tooltip}
      >
        {f.text}
      </span>
      <span className="rr-row-time">{relativeTime(job.createdAt)}</span>
      <div className="rr-cta-wrap" ref={menuRef}>
        <button
          type="button"
          className="rr-cta-btn"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label={`Open actions for ${job.siteName || hostnameFor(job.sourceUrl)}`}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenuOpen((v) => !v); }}
        >
          <MoreVerticalIcon />
        </button>
        {menuOpen && (
          <ul className="rr-cta-menu" role="menu">
            <li>
              <Link href={`/audit/${job.id}`} className="rr-cta-menu-item" role="menuitem" onClick={() => setMenuOpen(false)}>
                <ViewIcon /> Open audit
              </Link>
            </li>
            {job.status === "done" && (
              <li>
                <button
                  type="button"
                  className="rr-cta-menu-item"
                  role="menuitem"
                  onClick={async () => {
                    const ok = await copyShareLink(job.id);
                    if (ok) { setCopied(true); setTimeout(() => setCopied(false), 2000); }
                    setMenuOpen(false);
                  }}
                >
                  <ShareIcon /> {copied ? "Copied!" : "Share link"}
                </button>
              </li>
            )}
            <li>
              <button
                type="button"
                className="rr-cta-menu-item"
                role="menuitem"
                disabled={reRunning}
                onClick={() => { setMenuOpen(false); reAudit(); }}
              >
                <RerunIcon /> {reRunning ? "Starting…" : "Re-run audit"}
              </button>
            </li>
            <li>
              <button
                type="button"
                className="rr-cta-menu-item rr-cta-menu-danger"
                role="menuitem"
                onClick={() => { setMenuOpen(false); onDelete(); }}
              >
                <TrashIcon size={14} /> Delete
              </button>
            </li>
          </ul>
        )}
      </div>
    </li>
  );
}

/* ------------------------------------------------------------------ */
/*  Delete dialog                                                      */
/* ------------------------------------------------------------------ */

function DeleteDialog({ job, deleting, onCancel, onConfirm }: {
  job: JobSummary; deleting: boolean; onCancel: () => void; onConfirm: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !deleting) onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [deleting, onCancel]);

  return (
    <div className="confirm-overlay" role="dialog" aria-modal="true">
      <div className="confirm-card">
        <h3>Move to Trash?</h3>
        <p><b>{job.siteName || hostnameFor(job.sourceUrl)}</b> will be moved to Trash. You can restore it within 30 days.</p>
        <div className="confirm-actions">
          <button type="button" className="dash-btn dash-btn-ghost" onClick={onCancel} disabled={deleting}>Keep it</button>
          <button type="button" className="dash-btn dash-btn-danger" onClick={onConfirm} disabled={deleting}>{deleting ? "Moving…" : "Move to Trash"}</button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Skeleton                                                           */
/* ------------------------------------------------------------------ */

function DashboardSkeleton() {
  return (
    <div className="dash-page">
      <div className="dash-header">
        <div>
          <span className="sk sk-title" />
          <span className="sk sk-line" style={{ width: 320, height: 14, marginTop: 8 }} />
        </div>
      </div>
      <div className="dash-columns">
        <div className="dash-main">
          <div className="rc" style={{ minHeight: 260 }}>
            <span className="sk sk-line" style={{ width: 140, height: 20, marginBottom: 12 }} />
            <span className="sk" style={{ width: "100%", height: 52, borderRadius: 14, marginBottom: 16 }} />
            <span className="sk sk-line" style={{ width: "100%", height: 72, borderRadius: 12 }} />
          </div>
        </div>
        <aside className="dash-aside" aria-hidden="true">
          <div className="tip">
            <span className="sk sk-line" style={{ width: 88, height: 14, marginBottom: 12 }} />
            <span className="sk sk-line" style={{ width: "100%", height: 12, marginBottom: 8 }} />
            <span className="sk sk-line" style={{ width: "72%", height: 12 }} />
          </div>
        </aside>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Example preview illustration (SVG)                                 */
/* ------------------------------------------------------------------ */

function ExamplePreviewIllustration() {
  return (
    <svg width="100%" height="100%" viewBox="0 0 240 140" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      {/* Browser chrome */}
      <rect x="8" y="8" width="224" height="124" rx="6" fill="var(--card)" stroke="var(--line)" strokeWidth="1" />
      <rect x="8" y="8" width="224" height="20" rx="6" fill="var(--bg-soft)" />
      <rect x="8" y="22" width="224" height="6" fill="var(--bg-soft)" />
      <circle cx="20" cy="18" r="3" fill="#F96F5D" opacity="0.7" />
      <circle cx="30" cy="18" r="3" fill="#F5B731" opacity="0.7" />
      <circle cx="40" cy="18" r="3" fill="#2b8a8f" opacity="0.7" />
      {/* Content blocks */}
      <rect x="18" y="38" width="80" height="8" rx="2" fill="var(--line)" />
      <rect x="18" y="52" width="120" height="6" rx="2" fill="var(--line)" opacity="0.5" />
      <rect x="18" y="64" width="100" height="6" rx="2" fill="var(--line)" opacity="0.5" />
      {/* Finding dots */}
      <circle cx="170" cy="50" r="10" fill="#F96F5D" opacity="0.2" />
      <text x="170" y="54" textAnchor="middle" fontSize="10" fontWeight="700" fill="#F96F5D">1</text>
      <circle cx="200" cy="80" r="10" fill="#F5B731" opacity="0.2" />
      <text x="200" y="84" textAnchor="middle" fontSize="10" fontWeight="700" fill="#F5B731">2</text>
      <circle cx="50" cy="100" r="10" fill="#2b8a8f" opacity="0.2" />
      <text x="50" y="104" textAnchor="middle" fontSize="10" fontWeight="700" fill="#2b8a8f">3</text>
      {/* Sidebar hint */}
      <rect x="155" y="36" width="68" height="90" rx="4" fill="var(--bg-soft)" stroke="var(--line)" strokeWidth="0.5" />
      <rect x="161" y="42" width="56" height="5" rx="1.5" fill="var(--primary)" opacity="0.3" />
      <rect x="161" y="52" width="56" height="5" rx="1.5" fill="var(--accent)" opacity="0.3" />
      <rect x="161" y="62" width="56" height="5" rx="1.5" fill="var(--line)" opacity="0.5" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Inline SVG icons                                                   */
/* ------------------------------------------------------------------ */

function LinkIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function LayoutIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" />
    </svg>
  );
}

function LightbulbIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 18h6" /><path d="M10 22h4" /><path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A7 7 0 1 0 7.5 11.5c.76.76 1.23 1.52 1.41 2.5" />
    </svg>
  );
}

// CheckCircleIcon removed — it only fed the "What you get" feature
// list that was dropped from the new-user view.

function ViewIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function RerunIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  );
}

function ShareIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </svg>
  );
}

function TrashIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function MoreVerticalIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="12" cy="5" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="12" cy="19" r="2" />
    </svg>
  );
}

/* Settings-panel icons. Match the visuals on /audit/new so the inline
   form looks like a sibling of the standalone audit-config page. */

function DesktopIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}

function MobileIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="7" y="2" width="10" height="20" rx="2" /><line x1="11" y1="18" x2="13" y2="18" />
    </svg>
  );
}

function BothIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="4" width="14" height="10" rx="1.5" /><rect x="14" y="9" width="8" height="11" rx="1.5" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function UploadCloudIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M16 16l-4-4-4 4" />
      <path d="M12 12v9" />
      <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
    </svg>
  );
}

